import OVODuck from 'ducks/ovo-duck';
import NewBookingDuck from 'ducks/new-booking-duck';
import { combineEpics, ofType } from 'redux-observable';
import { mergeMap, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { DeliveryIDSelector, TxnIDSelector } from 'selectors/ovo-selector';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import paymentEndpoints from 'actions/payment-endpoints';
import { buildHeadersV2, buildAjaxCredentials } from 'helper/ajax-helper';
import { OVOError, OVOErrorCode } from './common-errors';

const startAuthEpic = (action$, state$) =>
  action$.pipe(
    ofType(OVODuck.types.START_AUTH),
    mergeMap(action => {
      const deliveryID = DeliveryIDSelector(state$.value);
      const txnID = TxnIDSelector(state$.value);

      return forkJoin(
        of(deliveryID),
        ajax({
          url: paymentEndpoints.getMFADetails(txnID),
          method: 'GET',
          headers: buildHeadersV2({
            isPax: true,
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(true),
          timeout: 3000,
        }).pipe(catchError(error => of({ error }))),
      );
    }),
    mergeMap(([deliveryID, { response, error }]) => {
      const MFAURL = _get(response, 'detail.data.url');
      const termURL = _get(response, 'detail.data.termURL');

      if (error || !(MFAURL && termURL)) {
        const errorMessage = error
          ? `${error}`
          : I18n.t('ovo.failToGetMFADetails');
        const deliveryError = new OVOError(
          errorMessage,
          OVOErrorCode.failToGetMFADetails,
        );
        return of(
          NewBookingDuck.creators.finishCreatingDeliveries(
            undefined,
            [deliveryID],
            undefined,
            {
              [deliveryID]: [deliveryError],
            },
          ),
        );
      }

      return of(OVODuck.creators.setMFADetails(MFAURL, termURL));
    }),
  );

const submitAuthCodeEpic = (action$, state$) =>
  action$.pipe(
    ofType(OVODuck.types.SUBMIT_AUTH_CODE),
    mergeMap(action => {
      const { authCode } = action;
      const deliveryID = DeliveryIDSelector(state$.value);
      const txnID = TxnIDSelector(state$.value);

      const payload = { authCode };
      const body = {
        txID: txnID,
        payload: JSON.stringify(payload),
      };
      return forkJoin(
        of(deliveryID),
        ajax({
          url: paymentEndpoints.confirmMFA(),
          method: 'POST',
          body: JSON.stringify(body),
          headers: buildHeadersV2({
            isPax: true,
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(true),
          timeout: 3000,
        }).pipe(catchError(error => of({ error }))),
      );
    }),
    mergeMap(([deliveryID, { response, error }]) => {
      const code = _get(response, 'detail.statusCode');
      const result = _get(response, 'detail.data.result');
      if (error || code !== 200 || result !== 'success') {
        const errorMessage = error
          ? `${error}`
          : I18n.t('ovo.failToVerifyMFA', { result });
        const deliveryError = new OVOError(
          errorMessage,
          code || OVOErrorCode.failToVerifyMFA,
        );
        return of(
          NewBookingDuck.creators.finishCreatingDeliveries(
            undefined,
            [deliveryID],
            undefined,
            {
              [deliveryID]: [deliveryError],
            },
          ),
        );
      }

      return of(OVODuck.creators.authSuccess());
    }),
  );

const authSuccessEpic = (action$, state$) =>
  action$.pipe(
    ofType(OVODuck.types.AUTH_SUCCESS),
    mergeMap(action => {
      const deliveryID = DeliveryIDSelector(state$.value);
      const txnID = TxnIDSelector(state$.value);
      return of(
        NewBookingDuck.creators.createDeliveries({
          deliveryIDs: [deliveryID],
          paymentTransactionID: txnID,
        }),
      );
    }),
  );

const authCancelEpic = (action$, state$) =>
  action$.pipe(
    ofType(OVODuck.types.AUTH_CANCEL),
    mergeMap(action => {
      const deliveryID = DeliveryIDSelector(state$.value);
      return of(
        NewBookingDuck.creators.finishCreatingDeliveries(
          undefined,
          [deliveryID],
          new OVOError(I18n.t('ovo.userCancelMFA'), OVOErrorCode.userCancelMFA),
          undefined,
        ),
      );
    }),
  );

export default combineEpics(
  startAuthEpic,
  submitAuthCodeEpic,
  authSuccessEpic,
  authCancelEpic,
);
